import {
    useBetSliderStore,
    useConfigurationStore,
    useJackpotStore,
    usePlayerInfoStore,
    useSpinButtonSectionStore,
} from '@stores'
import React, { useEffect, useMemo, useRef, useState } from 'react'

import { useFormatMoney, useOpacityToggle, useScaleToFit } from '@hooks'
import { cn, useShrinkTextToFit } from '@amusnet/react-ui'
import gsap from 'gsap'
import {
    ChangeOpacityComponents,
    GameUIMessageType,
    PlayerInfoAdditionalWinMoneyMessage,
    PlayerInfoLimitReachedMessage,
    PlayerInfoMysteryPendingMessage,
    PlayerInfoUpdateBalanceMessage,
    PlayerInfoUpdateMoneyColorMessage,
    PlayerInfoUpdateWinAmountAndBalanceMessage,
    PlayerInfoUpdateWinAmountMessage,
    PlayerInfoUpdateWinLabelMessage,
    SpinButtonState,
    ViewportType,
} from 'game-ui-types'
import styles from './PlayerInfo.module.css'
import { getBetValue } from '@utils/betOption'
import { useTranslation } from '@amusnet/translations/react'
import { translate, TranslationKey } from '@amusnet/translations'

const DEFAULT_BALANCE_LABEL_FONT_SIZE = 24

export const usePlayerInfoBalanceConfig = () => {
    const viewport = useConfigurationStore((state) => state.viewport)
    const gameType = useConfigurationStore((state) => state.gameType)
    const isFeatureRunning = useConfigurationStore((state) => state.isFeatureRunning)

    const isGambleActive = useSpinButtonSectionStore((state) => state.isGambleActive)

    const isJackpotStarted = useJackpotStore((state) => state.isJackpotStarted)
    const isJackpotEQRunning = useJackpotStore((state) => state.isJackpotEQStarted)
    const isJackpotRoundRunning = useJackpotStore((state) => state.isJackpotRoundRunning)

    const hideLastWin =
        viewport === ViewportType.MOBILE_PORTRAIT && !isJackpotEQRunning && (isJackpotStarted || isJackpotRoundRunning)

    // player info balance config
    const playerInfoBalanceConfigStyles = useConfigurationStore(
        (state) => state.getComponentConfig('PlayerInfoBalance').styles
    )
    const playerInfoBalanceConfigPosition = useConfigurationStore(
        (state) => state.getComponentConfig('PlayerInfoBalance').position
    )
    const playerInfoBalanceConfigDimensions = useConfigurationStore(
        (state) => state.getComponentConfig('PlayerInfoBalance').dimensions
    )

    // player info balance jackpot config
    const playerInfoBalanceJackpotConfigStyles = useConfigurationStore(
        (state) => state.getComponentConfig('PlayerInfoBalanceJackpot').styles
    )
    const playerInfoBalanceJackpotConfigPosition = useConfigurationStore(
        (state) => state.getComponentConfig('PlayerInfoBalanceJackpot').position
    )
    const playerInfoBalanceJackpotConfigDimensions = useConfigurationStore(
        (state) => state.getComponentConfig('PlayerInfoBalanceJackpot').dimensions
    )

    // player info balance gamble config
    const playerInfoBalanceGambleConfigStyles = useConfigurationStore(
        (state) => state.getComponentConfig('PlayerInfoBalanceGamble').styles
    )
    const playerInfoBalanceGambleConfigPosition = useConfigurationStore(
        (state) => state.getComponentConfig('PlayerInfoBalanceGamble').position
    )
    const playerInfoBalanceGambleConfigDimensions = useConfigurationStore(
        (state) => state.getComponentConfig('PlayerInfoBalanceGamble').dimensions
    )

    const shouldUseMobileGambleStyles =
        viewport === ViewportType.MOBILE_PORTRAIT && isGambleActive && !!window.useRedesignedGamble

    const playerInfoBalancePosition = shouldUseMobileGambleStyles
        ? playerInfoBalanceGambleConfigPosition
        : !isJackpotEQRunning && (isJackpotStarted || isJackpotRoundRunning) && playerInfoBalanceJackpotConfigPosition
          ? playerInfoBalanceJackpotConfigPosition
          : playerInfoBalanceConfigPosition

    const playerInfoBalanceDimensions = shouldUseMobileGambleStyles
        ? playerInfoBalanceGambleConfigDimensions
        : (isJackpotStarted || isJackpotRoundRunning) && playerInfoBalanceJackpotConfigDimensions
          ? playerInfoBalanceJackpotConfigDimensions
          : playerInfoBalanceConfigDimensions

    const playerInfoBalanceStyle = shouldUseMobileGambleStyles
        ? playerInfoBalanceGambleConfigStyles
        : !isJackpotEQRunning && (isJackpotStarted || isJackpotRoundRunning) && playerInfoBalanceJackpotConfigStyles
          ? playerInfoBalanceJackpotConfigStyles
          : playerInfoBalanceConfigStyles

    return useMemo(
        () => ({
            hideLastWin,
            shouldUseMobileGambleStyles,
            playerInfoBalancePosition,
            playerInfoBalanceDimensions,
            playerInfoBalanceStyle,
        }),
        [
            viewport,
            gameType,
            isGambleActive,
            isJackpotStarted,
            isJackpotEQRunning,
            isJackpotRoundRunning,
            isFeatureRunning,
        ]
    )
}

const PlayerInfo: React.FC = () => {
    // If id changed need to pass new id to PromoWrapper component
    const balanceId = 'balance-component'
    const formatMoney = useFormatMoney()
    const { t } = useTranslation()
    const [shouldApplyMysteryColor, setShouldApplyMysteryColor] = useState(false)
    const [additionalWinMoney, setAdditionalWinMoney] = useState(0)
    const [canShowAdditionalWin, setCanShowAdditionalWin] = useState(false)
    const [canShowLimitReached, setCanShowLimitReached] = useState(false)
    const [canShowMysteryPending, setCanShowMysteryPending] = useState(false)

    const viewport = useConfigurationStore((state) => state.viewport)
    const currency = useConfigurationStore((state) => state.currency)
    const hasExternalBetAmountPlayerInfo = useConfigurationStore((state) => state.hasExternalBetAmountPlayerInfo)
    const hasLastWinPlayerInfoPortrait = useConfigurationStore((state) => state.hasLastWinPlayerInfoPortrait)
    const betOptions = useConfigurationStore((state) => state.betOptions)
    const setConfiguration = useConfigurationStore((state) => state.setConfiguration)
    const shouldBalanceSubtractOnly = useConfigurationStore((state) => state.shouldBalanceSubtractOnly)
    const isReplayMode = useConfigurationStore((state) => state.isReplayMode)
    const spinButtonState = useSpinButtonSectionStore((state) => state.state)

    const activeIndex = useBetSliderStore((state) => state.activeIndex)

    const isJackpotEQRunning = useJackpotStore((state) => state.isJackpotEQStarted)

    // player info last win config
    const playerInfoLastWinConfigStyles = useConfigurationStore(
        (state) => state.getComponentConfig('PlayerInfoLastWin').styles
    )
    const playerInfoLastWinConfigPosition = useConfigurationStore(
        (state) => state.getComponentConfig('PlayerInfoLastWin').position
    )
    const playerInfoLastWinConfigDimensions = useConfigurationStore(
        (state) => state.getComponentConfig('PlayerInfoLastWin').dimensions
    )

    // player info last win gamble config
    const playerInfoLastWinGambleConfigStyles = useConfigurationStore(
        (state) => state.getComponentConfig('PlayerInfoLastWinGamble').styles
    )
    const playerInfoLastWinGambleConfigPosition = useConfigurationStore(
        (state) => state.getComponentConfig('PlayerInfoLastWinGamble').position
    )
    const playerInfoLastWinGambleConfigDimensions = useConfigurationStore(
        (state) => state.getComponentConfig('PlayerInfoLastWinGamble').dimensions
    )

    // player info wrapper class config
    const playerInfoWrapperClassConfigStyles = useConfigurationStore(
        (state) => state.getComponentConfig('PlayerInfoWrapperClass').styles
    )

    // player info last win money wrapper config
    const playerInfoLastWinMoneyWrapperConfigStyles = useConfigurationStore(
        (state) => state.getComponentConfig('PlayerInfoLastWinMoneyWrapper').styles
    )

    // player info win label config
    const playerInfoWinLabelConfigStyles = useConfigurationStore(
        (state) => state.getComponentConfig('PlayerInfoWinLabel').styles
    )

    // player info currency label config
    const playerInfoCurrencyLabelConfigStyles = useConfigurationStore(
        (state) => state.getComponentConfig('PlayerInfoCurrencyLabel').styles
    )

    // player info external bet amount config
    const playerInfoExternalBetAmountConfigStyles = useConfigurationStore(
        (state) => state.getComponentConfig('PlayerInfoExternalBetAmount').styles
    )
    const playerInfoExternalBetAmountConfigPosition = useConfigurationStore(
        (state) => state.getComponentConfig('PlayerInfoExternalBetAmount').position
    )
    const playerInfoExternalBetAmountConfigDimensions = useConfigurationStore(
        (state) => state.getComponentConfig('PlayerInfoExternalBetAmount').dimensions
    )

    // player info additional win amount config
    const playerInfoAdditionalWinAmountConfigStyles = useConfigurationStore(
        (state) => state.getComponentConfig('PlayerInfoAdditionalWinAmount').styles
    )
    const playerInfoAdditionalWinAmountConfigPosition = useConfigurationStore(
        (state) => state.getComponentConfig('PlayerInfoAdditionalWinAmount').position
    )
    const playerInfoAdditionalWinAmountConfigDimensions = useConfigurationStore(
        (state) => state.getComponentConfig('PlayerInfoAdditionalWinAmount').dimensions
    )

    const balanceLabelFontSizeConfig = useConfigurationStore(
        (state) => state.getComponentConfig('PlayerInfoBalanceLabel').dimensions?.fontSize
    )
    const balanceLabelFontSize = parseInt(balanceLabelFontSizeConfig ?? '', 10) || DEFAULT_BALANCE_LABEL_FONT_SIZE

    const {
        hideLastWin,
        shouldUseMobileGambleStyles,
        playerInfoBalancePosition,
        playerInfoBalanceDimensions,
        playerInfoBalanceStyle,
    } = usePlayerInfoBalanceConfig()

    const balance = usePlayerInfoStore((state) => state.balance)
    const subtractedBalance = usePlayerInfoStore((state) => state.subtractedBalance)
    const winAmount = usePlayerInfoStore((state) => state.winAmount)
    const winLabel = usePlayerInfoStore((state) => state.winLabel)
    const isLastWin = usePlayerInfoStore((state) => state.isLastWin)
    const updateBalance = usePlayerInfoStore((state) => state.updateBalance)
    const setSubtractedBalance = usePlayerInfoStore((state) => state.setSubtractedBalance)
    const updateSubtractedBalance = usePlayerInfoStore((state) => state.updateSubtractedBalance)
    const updateWinAmount = usePlayerInfoStore((state) => state.updateWinAmount)
    const updateWinLabel = usePlayerInfoStore((state) => state.updateWinLabel)
    const updateIsWinAmountAnimating = usePlayerInfoStore((state) => state.updateIsWinAmountAnimating)

    // declare refs
    const winAnimationTween = useRef<gsap.core.Tween | null>(null)
    const balanceAnimationTween = useRef<gsap.core.Tween | null>(null)
    const hasSubtractedBalanceForCurrentSpin = useRef(false)
    const hasSubtractedBalanceFromTurboState = useRef(false)
    const shouldIgnoreNextCollectBalanceUpdate = useRef(false)
    const playerInfoComponent = useRef<HTMLDivElement | null>(null)
    const balanceComponent = useRef<HTMLDivElement | null>(null)
    const balanceContentRef = useRef<HTMLDivElement | null>(null)
    const balanceLabelRef = useRef<HTMLSpanElement | null>(null)
    const balanceAmountRef = useRef<HTMLDivElement | null>(null)
    const winComponent = useRef<HTMLDivElement | null>(null)
    const winContentRef = useRef<HTMLDivElement | null>(null)
    const winLabelRef = useRef<HTMLSpanElement | null>(null)
    const winAmountRef = useRef<HTMLDivElement | null>(null)
    const additionalWinAmountRef = useRef<HTMLDivElement | null>(null)
    const externalBetAmountComponent = useRef<HTMLDivElement | null>(null)
    const externalBetAmountLabelRef = useRef<HTMLSpanElement | null>(null)
    const externalBetAmountRef = useRef<HTMLDivElement | null>(null)

    useOpacityToggle(ChangeOpacityComponents.PLAYER_INFO, playerInfoComponent)
    useOpacityToggle(ChangeOpacityComponents.LAST_WIN, winComponent)
    useOpacityToggle(ChangeOpacityComponents.BALANCE, balanceComponent)

    const { opacity: externalBetAmountOpacity, isComponentInteractive: externalBetAmountIsInteractive } =
        useOpacityToggle(ChangeOpacityComponents.EXTERNAL_BET_AMOUNT, externalBetAmountComponent)

    const mysteryColor = 'text-[#F1010E]'
    const displayedBalance = shouldBalanceSubtractOnly ? (subtractedBalance ?? balance) : balance
    const isCollectBalanceUpdateState =
        spinButtonState === SpinButtonState.SEND_COLLECT_STATE || spinButtonState === SpinButtonState.SEND_GAMBLE_STATE

    const updateDisplayedBalance = (money: number) => {
        if (shouldBalanceSubtractOnly) {
            // shouldBalanceSubtractOnly: keep the fake displayed balance separate after the initial balance value.
            if (subtractedBalance === null) {
                setSubtractedBalance(money)
            }

            updateBalance(money)

            return
        }

        updateBalance(money)
    }

    const handleAmountUpdate = (
        isBalanceAmountUpdate: boolean,
        {
            isAnimating,
            money,
            shouldAnimate = false,
            startingAmount = 0,
            time = 0,
        }: { isAnimating?: boolean; money: number; shouldAnimate?: boolean; startingAmount?: number; time?: number }
    ) => {
        if (isBalanceAmountUpdate && shouldBalanceSubtractOnly) {
            // shouldBalanceSubtractOnly: collect winnings should not increase the fake displayed balance.
            if (
                shouldIgnoreNextCollectBalanceUpdate.current ||
                isCollectBalanceUpdateState ||
                (subtractedBalance !== null && money > displayedBalance)
            ) {
                shouldIgnoreNextCollectBalanceUpdate.current = false

                // shouldBalanceSubtractOnly: freeze the fake balance before storing collect winnings as hidden real balance.
                if (subtractedBalance === null) {
                    setSubtractedBalance(displayedBalance)
                }

                updateBalance(money)

                return
            }

            // shouldBalanceSubtractOnly: clear the collect guard after the first balance response.
            shouldIgnoreNextCollectBalanceUpdate.current = false
            balanceAnimationTween.current?.progress(1).kill()
            balanceAnimationTween.current = null
            updateDisplayedBalance(money)

            return
        }

        const tween = isBalanceAmountUpdate ? balanceAnimationTween : winAnimationTween
        const setAmount = isBalanceAmountUpdate ? updateDisplayedBalance : updateWinAmount

        if (isAnimating) {
            if (tween.current && tween.current.isActive()) {
                if (money < tween.current.vars.value) {
                    updateIsWinAmountAnimating(false)
                    tween.current?.progress(1).kill()
                    tween.current = null
                    setAmount(money)
                } else {
                    // replaces the old updateTo() method which is deprecated in gsap3
                    tween.current.duration(tween.current.duration() - tween.current.time()).invalidate()
                    tween.current.vars.value = money
                    tween.current.restart()
                }
            }
        } else if (shouldAnimate) {
            updateIsWinAmountAnimating(!isBalanceAmountUpdate)
            tween.current?.progress(1).kill()

            const temp = { value: startingAmount }

            tween.current = gsap.to(temp, {
                value: money,
                duration: time,
                ease: 'none',
                onUpdate() {
                    setAmount(temp.value)
                },
            })
        } else {
            updateIsWinAmountAnimating(false)
            tween.current?.progress(1).kill()
            tween.current = null
            setAmount(money)
        }
    }

    const handleBalanceAndWinAmountUpdate = ({
        shouldAnimate,
        balanceMoney,
        startingBalanceAmount = 0,
        startingWinAmount = 0,
        winAmountMoney,
        time = 0,
    }: {
        shouldAnimate?: boolean
        balanceMoney: number
        startingBalanceAmount: number
        startingWinAmount: number
        winAmountMoney: number
        time?: number
    }) => {
        const shouldKeepDisplayedBalanceAfterCollect =
            shouldBalanceSubtractOnly &&
            (shouldIgnoreNextCollectBalanceUpdate.current ||
                isCollectBalanceUpdateState ||
                (subtractedBalance !== null && balanceMoney > displayedBalance))

        // shouldBalanceSubtractOnly: clear the collect guard after the first combined balance/win response.
        if (shouldBalanceSubtractOnly) {
            shouldIgnoreNextCollectBalanceUpdate.current = false
        }

        balanceAnimationTween.current?.progress(1).kill()
        balanceAnimationTween.current = null
        winAnimationTween.current?.progress(1).kill()
        winAnimationTween.current = null

        if (shouldBalanceSubtractOnly) {
            if (!shouldKeepDisplayedBalanceAfterCollect) {
                updateDisplayedBalance(balanceMoney)
            } else {
                // shouldBalanceSubtractOnly: freeze the fake balance before storing collect winnings as hidden real balance.
                if (subtractedBalance === null) {
                    setSubtractedBalance(displayedBalance)
                }

                updateBalance(balanceMoney)
            }

            if (!shouldAnimate) {
                updateIsWinAmountAnimating(false)
                updateWinAmount(winAmountMoney)

                return
            }

            const tempWinAmount = {
                value: startingWinAmount,
            }

            winAnimationTween.current = gsap.to(tempWinAmount, {
                duration: time,
                ease: 'none',
                value: winAmountMoney,
                onStart: () => {
                    updateIsWinAmountAnimating(true)
                },
                onUpdate: () => {
                    updateWinAmount(tempWinAmount.value)
                },
                onComplete: () => {
                    winAnimationTween.current?.progress(1).kill()
                    winAnimationTween.current = null
                    updateIsWinAmountAnimating(false)
                },
            })

            return
        }

        if (!shouldAnimate) {
            updateIsWinAmountAnimating(false)
            updateDisplayedBalance(balanceMoney)
            updateWinAmount(winAmountMoney)

            return
        }

        const tempBalanceAmount = {
            value: startingBalanceAmount,
        }

        balanceAnimationTween.current = gsap.to(tempBalanceAmount, {
            duration: time,
            ease: 'none',
            value: balanceMoney,
            onUpdate: () => {
                updateDisplayedBalance(tempBalanceAmount.value)
            },
            onComplete: () => {
                balanceAnimationTween.current?.progress(1).kill()
                balanceAnimationTween.current = null
            },
        })

        const tempWinAmount = {
            value: startingWinAmount,
        }

        winAnimationTween.current = gsap.to(tempWinAmount, {
            duration: time,
            ease: 'none',
            value: winAmountMoney,
            onStart: () => {
                updateIsWinAmountAnimating(true)
            },
            onUpdate: () => {
                updateWinAmount(tempWinAmount.value)
            },
            onComplete: () => {
                winAnimationTween.current?.progress(1).kill()
                winAnimationTween.current = null
                updateIsWinAmountAnimating(false)
            },
        })
    }

    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            const rawData = event.data
            const { type } = rawData

            // TODO shouldBalanceUpdateImmediately = expo tournament and finnish requirement - prevent subtracting win amount from balance
            // Should be removed when the proper implementation is done (handled in the base engine instead of here)
            // We do it here because it is the quickest solution
            // ! we should get shouldBalanceUpdateImmediately from configurationStore afterwards
            if (event.data.id === 'EventForGame') {
                if (event.data.data.command === 'show') {
                    const data: PlayerInfoUpdateBalanceMessage = {
                        type: GameUIMessageType.PLAYER_INFO_UPDATE_BALANCE,
                        isAnimating: false,
                        money: event.data.data.balance,
                    }

                    setConfiguration({ shouldBalanceUpdateImmediately: event.data.data.shouldBalanceUpdateImmediately })

                    handleAmountUpdate(true, data)
                }
            }

            // TODO: shouldBalanceUpdateImmediately - END

            const subtractBetFromDisplayedBalance = (shouldRespectDuplicateGuard = true) => {
                // shouldBalanceSubtractOnly: cascade games can emit multiple spin-start messages for one bet.
                if (shouldRespectDuplicateGuard && hasSubtractedBalanceForCurrentSpin.current) return 'skipped'

                const betAmount = window.bottle?.container?.MainModel?.totalBet?.()

                if (typeof betAmount !== 'number') return 'skipped'

                // shouldBalanceSubtractOnly: compare against the displayed balance, not the hidden real balance.
                const currentDisplayedBalance = subtractedBalance ?? balance

                // shouldBalanceSubtractOnly: do not subtract visually when the displayed balance cannot cover the bet.
                if (currentDisplayedBalance < betAmount) return 'insufficient'

                // shouldBalanceSubtractOnly: subtract the bet once from the displayed balance.
                // const updatedBalance = Math.max(currentDisplayedBalance - betAmount, 0)

                // shouldBalanceSubtractOnly: mark this spin cycle as already subtracted.
                hasSubtractedBalanceForCurrentSpin.current = true
                updateSubtractedBalance(betAmount)

                return 'subtracted'
            }

            switch (type) {
                case GameUIMessageType.COLLECT_CLICKED: {
                    if (!shouldBalanceSubtractOnly) break

                    // shouldBalanceSubtractOnly: mark the next balance increase as collect winnings, not spendable balance.
                    shouldIgnoreNextCollectBalanceUpdate.current = true
                    break
                }
                case GameUIMessageType.SPIN_STARTED:
                case GameUIMessageType.SPIN_BUTTON_SEND_TURBO_STATE: {
                    if (!shouldBalanceSubtractOnly) break

                    if (type === GameUIMessageType.SPIN_STARTED && hasSubtractedBalanceFromTurboState.current) break

                    if (
                        type === GameUIMessageType.SPIN_BUTTON_SEND_TURBO_STATE &&
                        hasSubtractedBalanceForCurrentSpin.current &&
                        !hasSubtractedBalanceFromTurboState.current
                    ) {
                        // shouldBalanceSubtractOnly: skip a turbo-state echo after SPIN_STARTED already subtracted.
                        hasSubtractedBalanceFromTurboState.current = true
                        break
                    }

                    // shouldBalanceSubtractOnly: repeated turbo-state messages are paid turbo spins without idle resets.
                    const shouldRespectDuplicateGuard = type !== GameUIMessageType.SPIN_BUTTON_SEND_TURBO_STATE

                    if (type === GameUIMessageType.SPIN_BUTTON_SEND_TURBO_STATE) {
                        hasSubtractedBalanceFromTurboState.current = true
                    }

                    const subtractResult = subtractBetFromDisplayedBalance(shouldRespectDuplicateGuard)

                    if (type === GameUIMessageType.SPIN_BUTTON_SEND_TURBO_STATE && subtractResult === 'insufficient') {
                        // shouldBalanceSubtractOnly: stop turbo before it keeps requesting bets with no fake balance.
                        window.postMessage({ type: GameUIMessageType.END_TURBO }, '*')
                    }

                    break
                }
                // shouldBalanceSubtractOnly: reset duplicate-subtract protection when the paid spin cycle ends.
                case GameUIMessageType.SPIN_BUTTON_SEND_COLLECT_STATE:
                case GameUIMessageType.SPIN_BUTTON_SEND_STOP_STATE:
                case GameUIMessageType.SPIN_BUTTON_SEND_STOP_STATE_WITH_WIN:
                case GameUIMessageType.SPIN_BUTTON_SEND_IDLE_STATE:
                case GameUIMessageType.SPIN_BUTTON_SEND_IDLE_WITHOUT_GAMBLE_STATE:
                case GameUIMessageType.SPIN_BUTTON_SEND_BEFORE_GAMBLE_STATE:
                case GameUIMessageType.SPIN_BUTTON_SEND_WIN_WITHOUT_GAMBLE_STATE: {
                    if (!shouldBalanceSubtractOnly) break

                    // shouldBalanceSubtractOnly: allow the next real spin to subtract its bet.
                    hasSubtractedBalanceForCurrentSpin.current = false
                    hasSubtractedBalanceFromTurboState.current = false
                    break
                }
                // shouldBalanceSubtractOnly: autoplay can start another paid spin without passing through idle state.
                case GameUIMessageType.SPIN_BUTTON_UPDATE_AUTOPLAY_COUNT:
                case GameUIMessageType.SPIN_BUTTON_AUTOPLAY_STOPPED: {
                    if (!shouldBalanceSubtractOnly) break

                    // shouldBalanceSubtractOnly: allow the next autoplay spin to subtract its bet.
                    hasSubtractedBalanceForCurrentSpin.current = false
                    hasSubtractedBalanceFromTurboState.current = false
                    break
                }
                case GameUIMessageType.PLAYER_INFO_UPDATE_BALANCE: {
                    // if (shouldBalanceUpdateImmediately) return // Finnish requirement
                    const data = rawData as PlayerInfoUpdateBalanceMessage

                    handleAmountUpdate(true, data)
                    break
                }
                case GameUIMessageType.PLAYER_INFO_UPDATE_WIN_AMOUNT: {
                    const data = rawData as PlayerInfoUpdateWinAmountMessage

                    handleAmountUpdate(false, data)
                    break
                }
                case GameUIMessageType.PLAYER_INFO_UPDATE_WIN_AMOUNT_AND_BALANCE: {
                    // if (shouldBalanceUpdateImmediately) return // Finnish requirement
                    const data = rawData as PlayerInfoUpdateWinAmountAndBalanceMessage

                    handleBalanceAndWinAmountUpdate(data)
                    break
                }
                case GameUIMessageType.PLAYER_INFO_UPDATE_WIN_LABEL: {
                    const data = rawData as PlayerInfoUpdateWinLabelMessage

                    // Remove the colon from the label to make it a valid translation key
                    // Needed for previous engine versions that pass translated text directly with colons
                    const label = data.value.replace(':', '')

                    updateWinLabel(translate(label as TranslationKey), data.isLastWin ?? false)
                    break
                }
                case GameUIMessageType.PLAYER_INFO_COMPLETE_ANIMATIONS: {
                    // TODO: check for scope
                    // const data = rawData as PlayerInfoCompleteAnimationsMessage
                    // if (data.scope !== GameUIMessageScope.PLAYER_INFO) return

                    winAnimationTween.current?.progress(1).kill()
                    winAnimationTween.current = null
                    balanceAnimationTween.current?.progress(1).kill()
                    balanceAnimationTween.current = null
                    break
                }
                case GameUIMessageType.PLAYER_INFO_UPDATE_MONEY_COLOR: {
                    const data = rawData as PlayerInfoUpdateMoneyColorMessage

                    setShouldApplyMysteryColor(data.payload.isRed)
                    break
                }
                case GameUIMessageType.PLAYER_INFO_ADDITIONAL_WIN_MONEY: {
                    const data = rawData as PlayerInfoAdditionalWinMoneyMessage

                    setAdditionalWinMoney(data.winAmount)
                    break
                }
                case GameUIMessageType.PLAYER_INFO_LIMIT_REACHED: {
                    const data = rawData as PlayerInfoLimitReachedMessage

                    setCanShowLimitReached(data.visible)
                    break
                }
                case GameUIMessageType.PLAYER_INFO_MYSTERY_PENDING: {
                    const data = rawData as PlayerInfoMysteryPendingMessage

                    setCanShowMysteryPending(data.visible)
                    break
                }
                case GameUIMessageType.EQ_FREESPINS_INTRO_ENDED:
                case GameUIMessageType.JACKPOT_EQ_RECOVERY: {
                    setCanShowAdditionalWin(true)
                    break
                }
                case GameUIMessageType.JACKPOT_EQ_ENDED: {
                    setCanShowAdditionalWin(false)
                    setCanShowLimitReached(false)
                    setCanShowMysteryPending(false)
                    break
                }
                default:
                    break
            }
        }

        window.addEventListener('message', handleMessage, false)

        return () => {
            window.removeEventListener('message', handleMessage, false)
        }
    }, [
        balance,
        isCollectBalanceUpdateState,
        setConfiguration,
        setSubtractedBalance,
        shouldBalanceSubtractOnly,
        subtractedBalance,
        updateSubtractedBalance,
        updateBalance,
        updateIsWinAmountAnimating,
        updateWinAmount,
        updateWinLabel,
    ])

    const balanceBounds = {
        width: parseInt(playerInfoBalanceDimensions!.width!, 10),
    }

    useShrinkTextToFit({
        refresh: [t('common.balance'), shouldUseMobileGambleStyles, balanceLabelFontSize, balanceBounds.width],
        designFontSize: balanceLabelFontSize,
        bounds: { ...balanceBounds },
        textRef: balanceLabelRef,
        disabled: shouldUseMobileGambleStyles,
    })

    useShrinkTextToFit({
        refresh: [displayedBalance, shouldUseMobileGambleStyles, balanceBounds.width],
        designFontSize: 40,
        bounds: { ...balanceBounds },
        textRef: balanceAmountRef,
        disabled: shouldUseMobileGambleStyles,
    })

    const winBounds = {
        width: parseInt(playerInfoLastWinConfigDimensions!.width!, 10),
    }

    useShrinkTextToFit({
        refresh: [winLabel, shouldUseMobileGambleStyles, winBounds.width],
        designFontSize: 24,
        bounds: { ...winBounds },
        textRef: winLabelRef,
        disabled: shouldUseMobileGambleStyles,
    })

    useShrinkTextToFit({
        refresh: [winAmount, shouldUseMobileGambleStyles, winBounds.width],
        designFontSize: 60,
        bounds: { ...winBounds },
        textRef: winAmountRef,
        disabled: shouldUseMobileGambleStyles,
    })

    const externalBetAmountBounds = {
        width: parseInt(playerInfoExternalBetAmountConfigDimensions!.width!, 10) * 0.95, // Allow 5% padding on the sides
    }

    useShrinkTextToFit({
        refresh: [t('gameui.bet'), externalBetAmountBounds.width],
        designFontSize: 18,
        bounds: { ...externalBetAmountBounds },
        textRef: externalBetAmountLabelRef,
    })

    useShrinkTextToFit({
        refresh: [betOptions[activeIndex], externalBetAmountBounds.width],
        designFontSize: 40,
        bounds: { ...externalBetAmountBounds },
        textRef: externalBetAmountRef,
    })

    const additionalWinAmountBounds = {
        width: parseInt(playerInfoAdditionalWinAmountConfigDimensions!.width!, 10),
    }

    useShrinkTextToFit({
        refresh: [additionalWinMoney, additionalWinAmountBounds.width],
        designFontSize: 32,
        bounds: { ...additionalWinAmountBounds },
        textRef: additionalWinAmountRef,
    })

    const balanceScale = useScaleToFit(balanceComponent, balanceContentRef, [
        displayedBalance,
        shouldUseMobileGambleStyles,
    ])
    const lastWinScale = useScaleToFit(winComponent, winContentRef, [winAmount, winLabel, shouldUseMobileGambleStyles])
    const lastWinNoGambleScale = useScaleToFit(winComponent, winAmountRef, [winAmount])

    const renderAdditionalWinAmount = (isMaxCapReached: boolean) => {
        return (
            <div
                id="additional-win-money"
                className={cn(styles.additionalWinContainer, !isMaxCapReached && styles.additionalWinWithoutMaxCap)}
                ref={additionalWinAmountRef}
                style={playerInfoAdditionalWinAmountConfigDimensions}
            >
                <span className={isMaxCapReached ? undefined : styles.gradientBackground}>
                    +{formatMoney(additionalWinMoney)}
                    <span className={styles.currency}>{currency}</span>
                </span>
            </div>
        )
    }

    const renderPendingStatus = () => {
        const text = canShowLimitReached
            ? t('gameui.platform.limitReachedNoFurtherWins')
            : t('gameui.platform.additionalWinPending')

        return (
            <div id="pending-win-status" className={styles.limitReachedContainer}>
                {text}
            </div>
        )
    }

    const renderWinMessages = () => {
        const showAdditionalWin = canShowAdditionalWin && additionalWinMoney !== 0
        const showPendingStatus = canShowLimitReached || canShowMysteryPending

        if (!showAdditionalWin && !showPendingStatus) {
            return null
        }

        if (!showPendingStatus) {
            return renderAdditionalWinAmount(false)
        }

        const raisedMarginBottom = viewport === ViewportType.DESKTOP ? '24px' : '20px'

        return (
            <div
                className={cn(
                    styles.winMessagesGroup,
                    isJackpotEQRunning && styles.additionalWinMaxCapMessagesGroupBackground,
                    playerInfoAdditionalWinAmountConfigStyles
                )}
                style={{
                    ...playerInfoAdditionalWinAmountConfigPosition,
                    ...(showAdditionalWin && { marginBottom: raisedMarginBottom }),
                }}
            >
                {showAdditionalWin && renderAdditionalWinAmount(true)}

                {renderPendingStatus()}
            </div>
        )
    }

    const renderBalance = () => {
        return (
            <div
                id={balanceId}
                ref={balanceComponent}
                className={cn(
                    'absolute flex flex-col items-center font-bold text-nowrap whitespace-nowrap text-center drop-shadow-[0_3px_0_rgba(0,0,0,0.45)] [-webkit-text-stroke:4px_rgba(0,0,0,0.32)] [paint-order:stroke_fill]',
                    playerInfoBalanceStyle
                )}
                style={{
                    ...playerInfoBalancePosition,
                    ...playerInfoBalanceDimensions,
                }}
            >
                <span id="balance-label" ref={balanceLabelRef} className="text-white text-[24px] leading-none">
                    {t('common.balance')}:
                </span>
                <div
                    className={cn('flex flex-col items-center', shouldApplyMysteryColor ? mysteryColor : 'text-white')}
                >
                    <div
                        id="balance-amount-field"
                        ref={balanceAmountRef}
                        className="text-[40px] leading-[46px] font-black"
                    >
                        {formatMoney(displayedBalance)}
                    </div>
                    <div
                        id="balance-currency-field"
                        className={cn(
                            'text-[24px] leading-none uppercase',
                            shouldApplyMysteryColor ? mysteryColor : 'text-white'
                        )}
                    >
                        {currency}
                    </div>
                </div>
            </div>
        )
    }

    const renderBalanceInGambleMobile = () => {
        return (
            <div
                id={balanceId}
                ref={balanceComponent}
                className={cn(
                    'absolute flex items-center font-bold text-nowrap whitespace-nowrap text-center drop-shadow-[0_4px_0_rgba(0,0,0,0.75)] [-webkit-text-stroke:5px_rgba(0,0,0,0.32)] [paint-order:stroke_fill]',
                    playerInfoBalanceStyle
                )}
                style={{
                    ...playerInfoBalancePosition,
                    ...playerInfoBalanceDimensions,
                }}
            >
                <div
                    ref={balanceContentRef}
                    style={{
                        transform: `scale(${balanceScale})`,
                        display: 'flex',
                    }}
                >
                    <span id="balance-label" ref={balanceLabelRef} className="text-xl font-bold mr-1 text-white">
                        {t('common.balance')}:
                    </span>
                    <div
                        className={cn('flex ml-1 items-center', shouldApplyMysteryColor ? mysteryColor : 'text-white')}
                    >
                        <div id="balance-amount-field" ref={balanceAmountRef} className="text-xl font-bold mr-1">
                            {formatMoney(displayedBalance)}
                        </div>
                        <div
                            id="balance-currency-field"
                            className={cn(
                                'text-xl text-white ml-1 uppercase',
                                shouldApplyMysteryColor ? mysteryColor : 'text-white'
                            )}
                        >
                            {currency}
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    const renderLastWin = () => {
        const hideWinOrLastWin = !hasLastWinPlayerInfoPortrait && isLastWin && viewport === ViewportType.MOBILE_PORTRAIT

        return (
            <div
                ref={winComponent}
                className={cn(
                    'absolute items-center font-bold text-nowrap whitespace-nowrap text-center drop-shadow-[0_3px_0_rgba(0,0,0,0.45)] [-webkit-text-stroke:4px_rgba(0,0,0,0.32)] [paint-order:stroke_fill]',
                    hideLastWin ? 'hidden' : 'flex',
                    playerInfoLastWinConfigStyles
                )}
                style={{
                    ...playerInfoLastWinConfigPosition,
                    ...playerInfoLastWinConfigDimensions,
                }}
            >
                {renderWinMessages()}

                {!hideWinOrLastWin && (
                    <>
                        <span
                            id="win-label"
                            ref={winLabelRef}
                            className={cn('text-white text-[24px] leading-none', playerInfoWinLabelConfigStyles)}
                        >
                            {winLabel ? `${winLabel}:` : '\u00A0'}
                        </span>
                        <div
                            className={cn(
                                playerInfoLastWinMoneyWrapperConfigStyles,
                                shouldApplyMysteryColor ? mysteryColor : 'text-white'
                            )}
                        >
                            <div
                                id="win-amount-field"
                                ref={winAmountRef}
                                className="text-[60px] leading-[46px]"
                                style={{
                                    opacity: winAmount === 0 ? 0 : 1,
                                    transform: `scale(${lastWinNoGambleScale})`,
                                }}
                            >
                                {formatMoney(winAmount)}
                            </div>
                            <div
                                id="win-currency-field"
                                className={cn(
                                    'text-[24px] leading-none uppercase',
                                    shouldApplyMysteryColor ? mysteryColor : 'text-white',
                                    playerInfoCurrencyLabelConfigStyles
                                )}
                                style={{ opacity: winAmount === 0 ? 0 : 1 }}
                            >
                                {currency}
                            </div>
                        </div>
                    </>
                )}
            </div>
        )
    }

    const renderExternalBetAmount = () => {
        return (
            <div
                ref={externalBetAmountComponent}
                className={cn(
                    'absolute items-center font-bold text-nowrap whitespace-nowrap text-center drop-shadow-[0_3px_0_rgba(0,0,0,0.45)] [-webkit-text-stroke:4px_rgba(0,0,0,0.32)] [paint-order:stroke_fill]',
                    playerInfoExternalBetAmountConfigStyles
                )}
                style={{
                    ...playerInfoExternalBetAmountConfigPosition,
                    ...playerInfoExternalBetAmountConfigDimensions,
                    opacity: externalBetAmountOpacity,
                    pointerEvents: externalBetAmountIsInteractive ? 'auto' : 'none',
                }}
            >
                <span
                    id="external-bet-amount-label"
                    ref={externalBetAmountLabelRef}
                    className="text-white text-[18px] leading-[14px]"
                >
                    {t('gameui.bet')}:
                </span>
                <div className="flex flex-col items-center text-white">
                    <div
                        id="external-bet-amount-field"
                        ref={externalBetAmountRef}
                        className="text-[40px] leading-[36px] font-black"
                    >
                        {formatMoney(getBetValue(betOptions[activeIndex]))}
                    </div>
                    <div
                        id="external-bet-amount-currency-field"
                        className="text-[16px] leading-[16px] text-white uppercase"
                    >
                        {currency}
                    </div>
                </div>
            </div>
        )
    }

    const renderLastWinInGambleMobile = () => {
        return (
            <div
                ref={winComponent}
                className={cn(
                    'absolute items-center font-bold text-nowrap whitespace-nowrap text-center drop-shadow-[0_3px_0_rgba(0,0,0,0.45)] [-webkit-text-stroke:4px_rgba(0,0,0,0.32)] [paint-order:stroke_fill]',
                    playerInfoLastWinGambleConfigStyles,
                    hideLastWin ? 'hidden' : 'flex'
                )}
                style={{
                    ...playerInfoLastWinGambleConfigPosition,
                    ...playerInfoLastWinGambleConfigDimensions,
                }}
            >
                <div
                    ref={winContentRef}
                    style={{
                        transform: `scale(${lastWinScale})`,
                        display: 'flex',
                    }}
                >
                    {renderWinMessages()}

                    <span id="win-label" ref={winLabelRef} className="text-xl font-bold mr-1 text-white">
                        {winLabel}:
                    </span>
                    <div
                        className={cn('flex items-center ml-1', shouldApplyMysteryColor ? mysteryColor : 'text-white')}
                    >
                        <div
                            id="win-amount-field"
                            ref={winAmountRef}
                            className="text-xl font-bold mr-1"
                            style={{ opacity: winAmount === 0 ? 0 : 1 }}
                        >
                            {formatMoney(winAmount)}
                        </div>
                        <div
                            id="win-currency-field"
                            className={cn(
                                'text-xl text-white ml-1 leading-none uppercase',
                                shouldApplyMysteryColor ? mysteryColor : 'text-white',
                                playerInfoCurrencyLabelConfigStyles
                            )}
                            style={{ opacity: winAmount === 0 ? 0 : 1 }}
                        >
                            {currency}
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div ref={playerInfoComponent} className={playerInfoWrapperClassConfigStyles}>
            {!isReplayMode && (shouldUseMobileGambleStyles ? renderBalanceInGambleMobile() : renderBalance())}
            {shouldUseMobileGambleStyles ? renderLastWinInGambleMobile() : renderLastWin()}
            {hasExternalBetAmountPlayerInfo && renderExternalBetAmount()}
        </div>
    )
}

export default PlayerInfo
